import { useState } from "react";
import axios from "axios";
import { ChevronDown } from "lucide-react";
import { Reveal } from "./Reveal";

const REASONS = [
  "Request a demo",
  "Clinical partnership",
  "Academic partnership",
  "Investment",
  "Collaboration",
  "General question",
];

const API = "/api/contact";
const EMPTY = {
  name: "",
  email: "",
  organization: "",
  reason: "",
  message: "",
  website: "",
};

const inputClass =
  "w-full rounded-2xl border border-brand-teal/15 bg-white px-5 py-4 text-[17px] font-medium text-brand-ink placeholder:text-brand-ink/40 focus:border-brand-coral focus:outline-none";

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      className="mt-1.5 text-[15px] font-bold text-brand-coral"
      data-testid={`${id}-text`}
    >
      {message}
    </p>
  );
}

export default function Contact() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((er) => (er[key] ? { ...er, [key]: "" } : er));
  };

  const validate = () => {
    const e = {};
    if (values.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim()))
      e.email = "Please enter a valid email address.";
    if (!values.reason) e.reason = "Please choose a reason for reaching out.";
    if (values.message.trim().length < 10)
      e.message = "Please enter a message of at least 10 characters.";
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = validate();
    setErrors(clientErrors);
    if (Object.values(clientErrors).some(Boolean)) return;
    setStatus("sending");
    try {
      await axios.post(API, values);
      setStatus("success");
    } catch (err) {
      const serverErrors = err.response?.data?.detail?.errors;
      if (serverErrors) setErrors(serverErrors);
      setStatus("failure");
    }
  };

  return (
    <section
      id="contact"
      className="scroll-mt-24 lg:scroll-mt-28 pb-20 md:pb-28"
      data-testid="contact-section"
    >
      <div className="symp-container">
        <div className="grid gap-12 lg:grid-cols-[42fr_54fr] lg:gap-16">
          <Reveal>
            <h2 className="section-title text-brand-teal">Contact</h2>
            <p className="mt-4 max-w-[460px] text-[18px] font-medium leading-[1.6] text-brand-ink md:text-[20px]">
              Send a message and the SympTrack team will get back to you.
            </p>
            <p className="mt-6 max-w-[460px] text-[15px] font-semibold leading-relaxed text-brand-ink/75">
              Please do not include medical records or sensitive health
              information in your message.
            </p>
          </Reveal>
          <Reveal delay={100}>
            {status === "success" ? (
              <div
                role="status"
                data-testid="contact-success-message"
                className="rounded-[32px] bg-brand-aqua/45 px-8 py-12 text-center"
              >
                <p className="text-[26px] font-extrabold text-brand-teal md:text-[30px]">
                  Thank you for reaching out.
                </p>
                <p className="mt-3 text-[18px] font-medium leading-[1.6] text-brand-ink md:text-[19px]">
                  Your message has been received. We will be in touch soon.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                data-testid="contact-form"
                className="rounded-[32px] bg-white/70 p-6 shadow-[0_18px_44px_rgba(7,88,102,0.07)] md:p-9"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="mb-2 block text-[16px] font-extrabold text-brand-ink"
                    >
                      Name <span className="text-brand-coral">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      autoComplete="name"
                      value={values.name}
                      onChange={set("name")}
                      aria-required="true"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? "contact-name-error" : undefined}
                      className={inputClass}
                      data-testid="contact-name-input"
                      placeholder="Your name"
                    />
                    <FieldError
                      id="contact-name-error"
                      message={errors.name}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="mb-2 block text-[16px] font-extrabold text-brand-ink"
                    >
                      Email <span className="text-brand-coral">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      autoComplete="email"
                      value={values.email}
                      onChange={set("email")}
                      aria-required="true"
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={errors.email ? "contact-email-error" : undefined}
                      className={inputClass}
                      data-testid="contact-email-input"
                      placeholder="Your email address"
                    />
                    <FieldError
                      id="contact-email-error"
                      message={errors.email}
                    />
                  </div>
                </div>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="contact-organization"
                      className="mb-2 block text-[16px] font-extrabold text-brand-ink"
                    >
                      Organization{" "}
                      <span className="text-[14px] font-semibold text-brand-ink/60">
                        (optional)
                      </span>
                    </label>
                    <input
                      id="contact-organization"
                      type="text"
                      autoComplete="organization"
                      value={values.organization}
                      onChange={set("organization")}
                      className={inputClass}
                      data-testid="contact-organization-input"
                      placeholder="Organization or practice"
                    />
                    <FieldError
                      id="contact-organization-error"
                      message={errors.organization}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-reason"
                      className="mb-2 block text-[16px] font-extrabold text-brand-ink"
                    >
                      Reason for reaching out{" "}
                      <span className="text-brand-coral">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="contact-reason"
                        value={values.reason}
                        onChange={set("reason")}
                        aria-required="true"
                        aria-invalid={Boolean(errors.reason)}
                        aria-describedby={errors.reason ? "contact-reason-error" : undefined}
                        className={`${inputClass} appearance-none pr-12 ${values.reason ? "" : "text-brand-ink/40"}`}
                        data-testid="contact-reason-select"
                      >
                        <option value="">Select a reason</option>
                        {REASONS.map((reason) => (
                          <option key={reason} value={reason}>
                            {reason}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        aria-hidden="true"
                        className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-teal"
                      />
                    </div>
                    <FieldError
                      id="contact-reason-error"
                      message={errors.reason}
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <label
                    htmlFor="contact-message"
                    className="mb-2 block text-[16px] font-extrabold text-brand-ink"
                  >
                    Message <span className="text-brand-coral">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    rows="5"
                    value={values.message}
                    onChange={set("message")}
                    aria-required="true"
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? "contact-message-error" : undefined}
                    className={`${inputClass} resize-y`}
                    data-testid="contact-message-textarea"
                    placeholder="Tell us a little about what brings you here"
                  />
                  <FieldError
                    id="contact-message-error"
                    message={errors.message}
                  />
                </div>
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    value={values.website}
                    onChange={set("website")}
                  />
                </div>
                {status === "failure" && (
                  <div
                    role="alert"
                    data-testid="contact-error-message"
                    className="mt-5 rounded-2xl bg-brand-coral/10 px-5 py-4 text-[16px] font-semibold leading-relaxed text-brand-ink"
                  >
                    Something went wrong while sending your message. Please try
                    again or email us directly at{" "}
                    <a
                      href="mailto:symptrackhealth@gmail.com"
                      className="font-extrabold underline underline-offset-4"
                      data-testid="contact-error-email-link"
                    >
                      symptrackhealth@gmail.com
                    </a>
                    .
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  aria-busy={status === "sending"}
                  data-testid="contact-submit-button"
                  className="btn-coral primary-action mt-7 h-[60px] w-full px-9 text-[18px] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  {status === "sending" ? "Sending..." : "Send message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
